import { Button } from 'antd'
import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import { useMemo } from 'react'
import { useAccount } from 'wagmi'

import { useLocalStorage } from '@/context/localstorage-context'
import type { PosStakeRegisterTransParamsInterface } from '@/types/trans-params'
import { wanPubKey2Address } from '@/utils'

export default function StepTwo_1({
  isPending,
  setStep,
  tarnsParam,
  handleSendTrans,
}: {
  isPending: boolean
  tarnsParam: PosStakeRegisterTransParamsInterface
  setStep: (val: number) => void
  handleSendTrans: () => void
}) {
  const { address } = useAccount()
  const { theme } = useLocalStorage()
  const isDark = useMemo(() => theme === 'dark', [theme])
  const btnCln = clsx('w-full flex justify-around mt-[20px]', {
    'btn1-light': !isDark,
    'btn1-dark': isDark,
  })

  return (
    <div>
      <div className="border-b-1 border-b-light-555 dark:border-b-dark-666">
        <p className="text-3xl text-light-111 mb-[0] dark:text-dark-111">
          Register & Deploy Node
        </p>
        <p className="text-2xl text-light-444">Confirm & Send</p>
      </div>
      {tarnsParam.isAcceptDelegation &&
        new BigNumber(tarnsParam.amount).lt(50000) && (
          <div className="text-light-108 mt-[10px] rounded-2xl py-4 px-6 bg-light-107 dark:bg-light-108 dark:text-black">
            <div>
              A delegating validator must have at least 50,000 WAN. Please top
              up your stake to 50,000 WAN or more after creation.
            </div>
          </div>
        )}
      <div className="border-1 border-light-555 rounded-2xl mt-[10px] p-[10px] dark:border-dark-666">
        <span className="dark:text-white">Validator Account</span>
        <div className="mt-[10px] text-[12px]">
          <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden dark:border-dark-666 dark:text-dark-0">
            <div className="whitespace-nowrap text-light-444 ">
              Public Key 1
            </div>
            <div className="flex-1 break-words text-right max-w-[80%] md:overflow-auto">
              {tarnsParam.secPk}
            </div>
          </div>
          <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden mt-3 dark:border-dark-666 dark:text-dark-0">
            <div className="whitespace-nowrap text-light-444">Public Key 2</div>
            <div className="flex-1 break-words text-right max-w-[80%] md:overflow-auto">
              {tarnsParam.bn256Pk}
            </div>
          </div>
          <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden mt-3 dark:border-dark-666 dark:text-dark-0">
            <div className="whitespace-nowrap text-light-444">
              Validator Account
            </div>
            <div className="flex-1 break-words text-right max-w-[80%] md:overflow-auto">
              {wanPubKey2Address(tarnsParam.secPk)}
            </div>
          </div>
          <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden mt-3 dark:border-dark-666 dark:text-dark-0">
            <div className="whitespace-nowrap text-light-444">Lock Period</div>
            <div className="flex-1 break-words text-right max-w-[80%]">
              {tarnsParam.lockEpochs} days
            </div>
          </div>
          <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden mt-3 dark:border-dark-666 dark:text-dark-0">
            <div className="whitespace-nowrap text-light-444">
              Accept Delegation
            </div>
            <div className="flex-1 break-words text-right max-w-[80%]">
              {tarnsParam.isAcceptDelegation ? 'Yes' : 'No'}
            </div>
          </div>
          {tarnsParam.isAcceptDelegation && (
            <div>
              <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden mt-3 dark:border-dark-666 dark:text-dark-0">
                <div className="whitespace-nowrap text-light-444">
                  Max Fee (%)
                </div>
                <div className="flex-1 break-words text-right max-w-[80%]">
                  {tarnsParam.maxFeeRate + '%'}
                </div>
              </div>
              <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden mt-3 dark:border-dark-666  dark:text-dark-0">
                <div className="whitespace-nowrap text-light-444">
                  Delegation Fee (%)
                </div>
                <div className="flex-1 break-words text-right max-w-[80%]">
                  {tarnsParam.feeRate + '%'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="border-1 border-light-555 rounded-2xl mt-[10px] p-[10px] dark:border-dark-666">
        <div className="dark:text-white">My Account</div>
        <div className="mt-[10px] text-[12px]">
          <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden dark:border-dark-666 dark:text-dark-0">
            <div className="whitespace-nowrap text-light-444">Address</div>
            <div className="flex-1 break-words text-right max-w-[80%]">
              {address}
            </div>
          </div>
          <div className="flex justify-between border-1 border-light-555 rounded-2xl w-full p-5 gap-5 overflow-hidden mt-3 dark:border-dark-666 dark:text-dark-0">
            <div className="whitespace-nowrap text-light-444">Amount</div>
            <div className="flex-1 break-words text-right max-w-[80%]">
              {tarnsParam.amount} WAN
            </div>
          </div>
        </div>
      </div>
      <div className={btnCln}>
        <Button
          onClick={() => setStep(11)}
          className="w-[49%] rounded-2xl h-[60px] text-[18px]"
        >
          Previous
        </Button>
        <Button
          loading={isPending}
          className="w-[49%] rounded-2xl h-[60px] btn text-[18px]"
          onClick={() => handleSendTrans()}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
